/**
 * 장면 전환 미리보기 — 작은 화면이 A·B 두 장면을 번갈아 넘기며 그 효과를 되풀이한다.
 *
 * **화면 플레이어와 같은 계획을 같은 방식으로 건다.** 효과마다 두 층이 어디서 어디로 가는지는 코어의
 * `transitionPlan` 이 정하고, 여기서도 플레이어(`apps/player-web/src/renderer.ts`)처럼 들어오는 층을 위로 올린 뒤
 * 두 층의 처음 모습을 확정하고 다음 프레임에 끝 모습을 주며, 다 움직이면 나간 층을 감추고 들어온 층을 맨 모습으로
 * 돌린다. 미리보기만 따로 흉내 내면 고른 것과 화면이 달라진다. 크기만 작다 — 밀기·덮기는 이 작은 화면의 폭만큼
 * 움직이고, 뒤집기의 원근·덮기의 그림자도 이 폭에 맞춰 줄어든다(`lookStyle`). 대각선 타일은 화면처럼 이전 장면을
 * 조각 사본으로 덮고(`tileGrid` — 같은 칸·같은 지연) 그 조각을 움직인다.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Play } from 'lucide-react';
import {
  cssTransition,
  HIDDEN,
  lookStyle,
  SHOWN,
  tileGrid,
  transitionPlan,
  transitionTotalMs,
  type LayerLook,
  type TilePlan,
  type Transition,
} from '@signageone/core';
import { Badge, Modal } from './ui.js';
import { TRANSITION_DESCRIPTIONS, TRANSITION_LABELS, TRANSITION_OPTIONS } from '../transitions.js';

/** 한 장면을 보여 주는 시간 — 효과를 보고, 넘어간 결과도 알아볼 만큼. */
const HOLD_MS = 1200;

function setLook(element: HTMLElement, look: LayerLook, width = 0): void {
  const style = lookStyle(look, width);
  element.style.opacity = style.opacity;
  element.style.transform = style.transform;
  element.style.boxShadow = style.boxShadow;
}

/** 이전 장면을 조각 사본으로 덮는다 — 화면 플레이어의 buildTiles 와 같은 칸·같은 지연(장면은 복제로). */
function buildTiles(stage: HTMLElement, from: HTMLElement, plan: TilePlan): { overlay: HTMLDivElement; start: () => void } {
  const width = stage.clientWidth;
  const height = stage.clientHeight;
  const overlay = document.createElement('div');
  overlay.className = 'tr-tiles';
  const cells = tileGrid(width, height, plan).map((tile) => {
    const cell = document.createElement('div');
    cell.className = 'tr-tile';
    cell.style.left = `${tile.x}px`;
    cell.style.top = `${tile.y}px`;
    cell.style.width = `${tile.width}px`;
    cell.style.height = `${tile.height}px`;
    const scene = from.cloneNode(true) as HTMLElement;
    // 장면은 무대를 가득 채우는 자리(사방 0)라 조각 안에서는 무대 크기로 다시 놓고 제 자리만큼 민다.
    scene.style.cssText = `left:${-tile.x}px;top:${-tile.y}px;right:auto;bottom:auto;width:${width}px;height:${height}px;`;
    cell.appendChild(scene);
    overlay.appendChild(cell);
    return { cell, delayMs: tile.delayMs };
  });
  stage.appendChild(overlay);
  return {
    overlay,
    start: () => {
      for (const { cell, delayMs } of cells) {
        cell.style.transition = cssTransition({ ...plan.motion, delayMs });
        setLook(cell, plan.to);
      }
    },
  };
}

export function TransitionPreview({ kind }: { kind: Transition }): ReactNode {
  const stage = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = stage.current;
    const [first, second] = root ? Array.from(root.querySelectorAll<HTMLDivElement>('.tr-scene')) : [];
    if (!root || !first || !second) return;

    let front = first;
    let back = second;
    for (const scene of [first, second]) scene.style.transition = 'none';
    setLook(front, SHOWN);
    setLook(back, HIDDEN);

    let timer = 0;
    let settle = 0;
    let frame = 0;
    let tiles: { overlay: HTMLDivElement; start: () => void } | null = null;
    const removeTiles = (): void => {
      tiles?.overlay.remove();
      tiles = null;
    };
    const step = (): void => {
      const plan = transitionPlan(kind);
      const width = root.clientWidth;
      // 들어오는 장면을 위로 — 덮기·확대·페이드는 새 장면이 이전 장면 위에 와야 한다.
      root.insertBefore(back, front.nextSibling);
      // 대각선 타일 — 이전 장면의 조각 사본을 맨 위에 얹는다(두 장면은 그 밑에서 바뀐다).
      removeTiles();
      if (plan?.tiles) tiles = buildTiles(root, front, plan.tiles);
      back.style.transition = 'none';
      setLook(back, plan ? plan.enterFrom : SHOWN, width);
      if (plan) {
        front.style.transition = 'none';
        setLook(front, plan.exitFrom, width);
      }
      void back.offsetWidth;
      frame = window.requestAnimationFrame(() => {
        const entering = back;
        const leaving = front;
        if (plan) {
          entering.style.transition = cssTransition(plan.enter);
          leaving.style.transition = cssTransition(plan.exit);
          setLook(entering, plan.enterTo, width);
          setLook(leaving, plan.exitTo, width);
          tiles?.start();
          // 다 움직였으면 화면처럼 — 나간 장면은 감추고, 들어온 장면은 그림자·원근 없는 맨 모습으로. 조각 사본도 걷는다.
          settle = window.setTimeout(() => {
            removeTiles();
            leaving.style.transition = 'none';
            setLook(leaving, HIDDEN);
            entering.style.transition = 'none';
            setLook(entering, SHOWN);
          }, transitionTotalMs(plan) + 50);
        } else {
          leaving.style.transition = 'none';
          setLook(leaving, HIDDEN);
        }
        [front, back] = [back, front];
        timer = window.setTimeout(step, (plan ? transitionTotalMs(plan) : 0) + HOLD_MS);
      });
    };
    timer = window.setTimeout(step, HOLD_MS);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(settle);
      window.cancelAnimationFrame(frame);
      removeTiles();
    };
  }, [kind]);

  return (
    <div className="tr-stage" ref={stage} role="img" aria-label={`${TRANSITION_LABELS[kind]} 효과 미리보기`}>
      <div className="tr-scene tr-scene-a">
        <span className="tr-scene-tag">A</span>
        <span className="tr-scene-line" />
      </div>
      <div className="tr-scene tr-scene-b">
        <span className="tr-scene-tag">B</span>
        <span className="tr-scene-line" />
      </div>
    </div>
  );
}

/**
 * [효과 미리보기] — 효과마다 움직이는 작은 화면과 설명을 한 번에 보여 준다.
 *
 * `onPick` 을 주면 카드에서 [이 효과 고르기] 로 고를 수 있다 — 편집기의 일괄 칸에 넣고, 목록에는 [전체 적용] 이 넣는다.
 */
export function TransitionGuideButton({
  current,
  onPick,
}: {
  /** 지금 목록의 효과. 줄마다 다르면 빈 문자열. */
  current: Transition | '';
  onPick?: (kind: Transition) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="btn btn-tiny" onClick={() => setOpen(true)}>
        <Play className="h-3.5 w-3.5" aria-hidden="true" /> 효과 미리보기
      </button>
      {open ? (
        <Modal title="전환 효과 미리보기" wide onClose={() => setOpen(false)}>
          <p className="hint">
            작은 화면이 A 와 B 를 번갈아 넘기며 효과를 되풀이합니다. 실제 화면도 같은 움직임으로 넘어갑니다 — 페이드는
            0.5초, 대각선 타일은 0.8초, 나머지는 0.7초입니다.
          </p>
          <ul className="tr-grid">
            {TRANSITION_OPTIONS.map((option) => {
              const isCurrent = current === option.value;
              return (
                <li key={option.value} className={`tr-card${isCurrent ? ' is-current' : ''}`}>
                  <TransitionPreview kind={option.value} />
                  <div className="tr-card-body">
                    <div className="tr-card-title">
                      <strong>{option.label}</strong>
                      {option.value === 'fade' ? <Badge>기본</Badge> : null}
                      {isCurrent ? <Badge tone="brand">지금 효과</Badge> : null}
                    </div>
                    <p className="tr-card-text">{TRANSITION_DESCRIPTIONS[option.value]}</p>
                    {onPick ? (
                      <button
                        type="button"
                        className="btn btn-tiny"
                        disabled={isCurrent}
                        onClick={() => {
                          onPick(option.value);
                          setOpen(false);
                        }}
                      >
                        이 효과 고르기
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="hint">
            옛 에이전트(0.7.7 이하)와 Apple TV 화면은 페이드 말고 다른 효과를 페이드로 넘깁니다 — 멈추거나 깨지지는
            않습니다.
          </p>
        </Modal>
      ) : null}
    </>
  );
}
